<template>
  <div
    class="item-container"
    :class="{
      'indented-item': isInsideSeparator && item.type !== ITEM_TYPES.SEPARATOR,
    }"
  >
    <v-card
      v-if="isVisible"
      class="px-6 pb-4 rounded-lg"
      data-testid="checklistItem-card-main"
      outlined
      :style="cardStyle"
      @click.native="$emit('click')"
    >
      <div class="d-flex align-center justify-center item-drag-control py-0">
        <v-icon color="blue-grey" size="24">mdi-drag-horizontal</v-icon>
      </div>

      <v-row class="no-gutters">
        <v-col class="me-0 pe-0">
          <v-lazy>
            <v-textarea
              auto-grow
              data-testid="checklistItem-textarea-name"
              required
              rows="1"
              :class="{
                'mb-2 body-1 font-weight-600':
                  item.type === ITEM_TYPES.SEPARATOR,
              }"
              :dense="item.type !== ITEM_TYPES.SEPARATOR"
              :disabled="disabled"
              :flat="item.type === ITEM_TYPES.SEPARATOR"
              :hide-details="item.type === ITEM_TYPES.SEPARATOR ? 'auto' : true"
              :label="
                item.type === ITEM_TYPES.SEPARATOR
                  ? `${$t('checklist.types.separator')}*`
                  : `${$t('checklist.question')}*`
              "
              :outlined="item.type !== ITEM_TYPES.SEPARATOR"
              :rules="rules"
              :solo="item.type === ITEM_TYPES.SEPARATOR"
              :value="item.name"
              @input="$emit('change', { name: $event })"
            >
            </v-textarea>
          </v-lazy>
        </v-col>

        <v-col
          v-if="item.type !== ITEM_TYPES.SEPARATOR"
          cols="5"
          class="ms-4 ps-0 text-right"
        >
          <v-row class="ma-0 pa-0" justify="end">
            <v-select
              outlined
              required
              dense
              hide-details
              item-value="id"
              :disabled="disabled"
              :value="item.type"
              :label="`${$t('common.type')}*`"
              :items="types"
              :rules="rules"
              @input="handleTypeChange"
            >
              <template v-slot:selection="{ item }">
                <v-icon
                  class="mr-2"
                  :class="getTypeIconClass(item)"
                  :size="getTypeIconSize(item)"
                  color="blue-grey darken-1"
                >
                  {{ item.icon }}
                </v-icon>
                <span
                  class="item-type-label ma-0 pa-0 blue-grey--text text--darken-3 body-1"
                  data-testid="checklistItem-text-typeLabel"
                >
                  {{ $t(`checklist.types.${item.id}`) }}
                </span>
              </template>
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-on="on" v-bind="attrs">
                  <v-list-item-icon class="mr-2">
                    <v-icon
                      :class="getTypeIconClass(item)"
                      :size="getTypeIconSize(item)"
                      color="blue-grey darken-1"
                    >
                      {{ item.icon }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title
                      class="item-type-label ma-0 pa-0 blue-grey--text text--darken-3 body-1"
                      >{{ $t(`checklist.types.${item.id}`) }}</v-list-item-title
                    >
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>
          </v-row>
          <div
            class="ma-0 pa-0 d-flex text-end align-center justify-end mt-6"
            v-if="!isConditionalLogicEnabled && !hasOptionsOrScale(item.type)"
          >
            <label
              class="body-1 blue-grey--text text--darken-1 me-2"
              :for="'required' + index"
              >{{ $t('common.required') }}</label
            >
            <v-switch
              hide-details
              class="ma-0 pa-0"
              :id="'required' + index"
              :input-value="item.isRequired"
              @change="$emit('change', { isRequired: $event })"
            />
          </div>
        </v-col>
      </v-row>

      <div
        v-if="item.type === ITEM_TYPES.SEPARATOR && shouldShowConditionButton"
        class="d-flex align-center justify-end mt-2 mb-4 ms-3"
        data-testid="checklistItem-row-sectionActions"
      >
        <Button
          class="me-auto"
          color="primaryLighten"
          data-testid="checklistItem-button-toggleCondition"
          icon="mdi-eye-outline"
          size="small"
          :disabled="disabled"
          :text="$t('checklist.visibility_rule.set_visibility_rule')"
          @click="handleToggleCondition"
        />
      </div>

      <v-row class="mt-2 mb-4" v-if="descriptionIsActive || item.description">
        <v-col>
          <v-lazy>
            <v-textarea
              v-model="item.description"
              required
              dense
              no-resize
              auto-grow
              hide-details
              outlined
              rows="3"
              :disabled="disabled"
              :label="`${$t('common.description')}*`"
            >
            </v-textarea>
          </v-lazy>
        </v-col>
      </v-row>

      <v-row class="mt-6 mb-4" v-if="hasOptionsOrScale(item.type)">
        <v-col>
          <Options
            v-if="itemHasOptions(item.type)"
            :hideNotificationAndFindingButtons="
              hideNotificationAndFindingButtons
            "
            :options="item.options"
            :type="item.type"
            :disabled="disabled"
            :active="active"
            @change="changeOption"
            @add="addOption"
            @remove="removeOption"
          />
          <Scale
            data-testid="checklistItem-scale-config"
            v-if="item.type === ITEM_TYPES.SCALE"
            :disabled="disabled"
            :max="item.max"
            :min="item.min"
            @change="handleScaleInput"
            @commit="handleScaleChange"
          />
          <DateTimeConfig
            v-if="item.type === ITEM_TYPES.DATE"
            :includeTime="item.includeTime"
            :disabled="disabled"
            @change="$emit('change', $event)"
          />
          <v-row
            class="ma-0 pa-0 mb-4"
            justify="end"
            v-if="!isConditionalLogicEnabled"
          >
            <label
              class="body-1 blue-grey--text text--darken-1 me-2"
              :for="'required' + index"
              >{{ $t('common.required') }}</label
            >
            <v-switch
              hide-details
              class="ma-0 pa-0"
              :id="'required' + index"
              :input-value="item.isRequired"
              @change="$emit('change', { isRequired: $event })"
            />
          </v-row>
        </v-col>
      </v-row>

      <v-row
        v-if="item.referenceMedia && item.referenceMedia.length"
        class="mt-4 mb-0"
        data-testid="checklistItem-row-referenceMedia"
      >
        <v-col>
          <MediaContainer
            onlyThumbnails
            smallThumbnails
            :readOnly="disabled"
            :images="item.referenceMedia"
            @change="handleMediaChange"
          />
        </v-col>
      </v-row>

      <v-divider v-if="item.type !== ITEM_TYPES.SEPARATOR" class="my-4" />

      <v-row
        v-if="shouldShowConditionalEditor"
        class="my-4"
        no-gutters
        data-testid="checklistItem-row-conditionalEditor"
      >
        <v-col :class="{ 'mx-2': item.type === ITEM_TYPES.SEPARATOR }">
          <ConditionalEditor
            data-testid="checklistItem-component-conditionalEditor"
            :disabled="disabled"
            :eligibleTriggers="eligibleTriggers"
            :isSaving="isSaving"
            :item="item"
            @change="handleRulesChange"
            @discard="handleConditionDiscard"
            @remove="handleRuleRemove"
          />
        </v-col>
      </v-row>

      <div
        v-if="isConditionalLogicEnabled && item.type !== ITEM_TYPES.SEPARATOR"
        class="d-flex align-center justify-end mt-4 mb-2"
      >
        <Button
          v-if="shouldShowConditionButton"
          class="me-auto ms-2"
          color="primaryLighten"
          data-testid="checklistItem-button-toggleCondition"
          icon="mdi-eye-outline"
          size="small"
          :disabled="disabled"
          :text="$t('checklist.visibility_rule.set_visibility_rule')"
          @click="handleToggleCondition"
        />

        <span
          class="me-auto d-flex align-center"
          data-testid="checklistItem-badge-triggerDependents"
          v-if="shouldShowTriggerBadge"
        >
          <v-icon class="me-2" color="blue-grey darken-1" size="18">
            mdi-information-outline
          </v-icon>
          <span class="caption blue-grey--text text--darken-1">
            {{ triggerBadgeText }}
          </span>
        </span>

        <label
          class="body-1 blue-grey--text text--darken-1 me-2"
          :for="'required' + index"
          >{{ $t('common.required') }}</label
        >

        <v-switch
          class="ma-0 pa-0"
          data-testid="checklistItem-switch-required"
          hide-details
          :id="'required' + index"
          :input-value="item.isRequired"
          @change="$emit('change', { isRequired: $event })"
        />
      </div>
    </v-card>

    <v-card
      v-else
      data-testid="checklistItem-card-loading"
      class="px-6 pb-4 rounded-lg"
      outlined
      :style="cardStyle"
      @click.native="$emit('click')"
    >
      <v-card-text>
        {{ $t('common.loading') }}
      </v-card-text>
    </v-card>
    <ItemMenu
      v-if="isVisible"
      class="options-menu"
      :disabled="disabled"
      :hasDescriptionActive="descriptionIsActive"
      :referenceMedia="item.referenceMedia"
      :type="item.type"
      @addItem="$emit('add')"
      @addDescription="handleAddDescription"
      @addMedia="handleMediaChange"
      @addSeparator="$emit('add', ITEM_TYPES.SEPARATOR)"
      @copyItem="$emit('copy', item)"
      @copySection="$emit('copySection')"
      @removeItem="$emit('remove')"
    />
  </div>
</template>

<script>
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import Button from '@/components/UI/Button'
import { utils } from 'frontend-js-shared'

import Options from './Options.vue'
import Scale from './Scale.vue'
import DateTimeConfig from './DateTimeConfig.vue'
import ItemMenu from './ItemMenu.vue'
import ConditionalEditor from './ConditionalEditor.vue'
import { ITEM_TYPES } from './constants.js'
import { createOption } from './optionIds'

export default {
  name: 'Item',

  components: {
    MediaContainer,
    Options,
    ItemMenu,
    Scale,
    DateTimeConfig,
    ConditionalEditor,
    Button,
  },

  props: {
    item: { type: Object, required: true },
    active: { type: Boolean, required: false },
    disabled: { type: Boolean, required: false },
    hideNotificationAndFindingButtons: { type: Boolean, required: false },
    index: Number,
    onlyShowIfInViewport: Boolean,
    isInsideSeparator: { type: Boolean, default: false },
    sectionBorderColor: { type: String, default: null },
    isConditionalLogicEnabled: { type: Boolean, default: false },
    eligibleTriggers: { type: Array, default: () => [] },
    dependentCount: { type: Number, default: 0 },
    isSaving: { type: Boolean, default: false },
  },

  data() {
    return {
      ITEM_TYPES,
      descriptionIsActive: Boolean(this.item?.description?.length),
      rules: [
        value =>
          Boolean(value?.trim?.()) ||
          this.$t('validation.form.this_field_is_required'),
      ],
      options: [],
      isVisible: !this.onlyShowIfInViewport,
      conditionEditorActive: false,
      // The editor reports unsaved pending work with its rule changes; the
      // editor must stay open while such work survives an empty-rule change.
      editorHasPendingDraft: false,
      pendingScaleChange: null,
    }
  },

  computed: {
    types() {
      return [
        {
          icon: 'mdi-text-short',
          id: ITEM_TYPES.TEXT,
        },
        { divider: true },
        {
          icon: 'mdi-radiobox-marked',
          id: ITEM_TYPES.RADIOS,
          hasOptions: true,
        },
        {
          icon: 'mdi-checkbox-marked-outline',
          id: ITEM_TYPES.CHECKBOXES,
          hasOptions: true,
        },
        {
          icon: 'mdi-arrow-down-drop-circle-outline',
          id: ITEM_TYPES.SELECT,
          hasOptions: true,
        },
        { divider: true },
        {
          icon: 'linear_scale',
          id: ITEM_TYPES.SCALE,
        },
        { divider: true },
        {
          icon: 'mdi-pound',
          id: ITEM_TYPES.NUMBER,
        },
        { divider: true },
        {
          icon: 'mdi-calendar',
          id: ITEM_TYPES.DATE,
        },
        ...this.getSignatureTypeOption(),
        { divider: true },
        {
          icon: 'mdi-image-outline',
          id: ITEM_TYPES.IMAGE,
        },
        { divider: true },
        {
          icon: 'mdi-account-outline',
          id: ITEM_TYPES.ACCOUNT,
        },
      ]
    },

    sectionBorderWidthStyle() {
      if (!this.sectionBorderColor) {
        return ''
      }

      if (this.item.type === ITEM_TYPES.SEPARATOR) {
        return 'border-left-width: 4px !important; '
      }

      return 'border-top-width: 4px !important; '
    },

    sectionBorderColorStyle() {
      if (!this.sectionBorderColor) {
        return ''
      }

      if (this.item.type === ITEM_TYPES.SEPARATOR) {
        return `border-left-color: ${this.sectionBorderColor} !important; `
      }

      return `border-top-color: ${this.sectionBorderColor} !important; `
    },

    cardStyle() {
      if (this.active) {
        return `border-color: #167DEC !important; ${this.sectionBorderWidthStyle}box-shadow: inset 0 0 0 1px #167DEC !important;`
      }

      if (!this.sectionBorderColor) {
        return ''
      }

      return this.sectionBorderWidthStyle + this.sectionBorderColorStyle
    },

    hasVisibilityRule() {
      return utils.checklists.hasVisibilityRule(this.item)
    },

    hasEligibleTriggers() {
      return this.eligibleTriggers.length > 0
    },

    shouldShowConditionalEditor() {
      return (
        this.isConditionalLogicEnabled &&
        (this.hasVisibilityRule || this.conditionEditorActive)
      )
    },

    hasDependents() {
      return this.isConditionalLogicEnabled && this.dependentCount > 0
    },

    shouldShowConditionButton() {
      return (
        this.isConditionalLogicEnabled &&
        this.hasEligibleTriggers &&
        !this.hasVisibilityRule &&
        !this.conditionEditorActive &&
        !this.hasDependents
      )
    },

    shouldShowTriggerBadge() {
      return this.hasDependents
    },

    triggerBadgeText() {
      return this.$t('checklist.visibility_rule.trigger_badge', {
        count: this.dependentCount,
      })
    },
  },

  watch: {
    hasVisibilityRule(value) {
      if (!value && !this.editorHasPendingDraft) {
        this.conditionEditorActive = false
      }
    },
  },

  mounted() {
    if (this.onlyShowIfInViewport) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting
        },
        { rootMargin: '200px' }
      )

      this.observer.observe(this.$el)
    }
  },

  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },

  methods: {
    getSignatureTypeOption() {
      return [
        { divider: true },
        {
          icon: 'mdi-signature-freehand',
          id: ITEM_TYPES.SIGNATURE,
        },
      ]
    },

    changeOption({ index, data }) {
      this.options = [...this.item.options]
      this.options[index] = {
        ...this.options[index],
        ...data,
      }
      this.$emit('change', { options: this.options })
    },

    itemHasOptions(itemType) {
      return (
        this.types.find(type => type.id === itemType)?.hasOptions ?? false
      )
    },

    hasOptionsOrScale(type) {
      return (
        this.itemHasOptions(type) ||
        type === ITEM_TYPES.SCALE ||
        type === ITEM_TYPES.DATE
      )
    },

    getTypeIconSize(typeItem) {
      return typeItem?.id === ITEM_TYPES.SIGNATURE ? 22 : 24
    },

    getTypeIconClass(typeItem) {
      return typeItem?.id === ITEM_TYPES.SIGNATURE
        ? 'item-type-icon-signature'
        : ''
    },

    handleAddDescription() {
      this.descriptionIsActive = !this.descriptionIsActive
      if (!this.descriptionIsActive) {
        this.$emit('change', { description: '' })
      }
    },

    handleMediaChange(referenceMedia) {
      this.$emit('change', { referenceMedia })
    },

    addOption() {
      if (this.hasDependents) {
        this.$emit('requestConditionalRemovalConfirmation', {
          change: { options: [...this.item.options, createOption()] },
        })
        return
      }

      this.options = [...this.item.options, createOption()]
      this.$emit('change', { options: this.options })
    },

    removeOption(index) {
      if (this.hasDependents) {
        const nextOptions = [...this.item.options]
        nextOptions.splice(index, 1)
        this.$emit('requestConditionalRemovalConfirmation', {
          change: { options: nextOptions },
        })
        return
      }

      this.options = [...this.item.options]
      this.options.splice(index, 1)
      this.$emit('change', { options: this.options })
    },

    addOptions() {
      this.options = [createOption()]
      this.$emit('change', { options: this.options })
    },

    handleTypeChange(type) {
      if (this.hasDependents) {
        this.$emit('requestConditionalRemovalConfirmation', {
          change: { type },
        })
        return
      }

      if (this.itemHasOptions(type) && !this.item?.options?.length)
        this.addOptions()
      this.$emit('change', { type })
    },

    // Scale drafts arrive per keystroke: with dependents they are buffered
    // until the bound is committed (blur/Enter), so the confirmation dialog
    // receives the complete value instead of the first partial keystroke.
    handleScaleInput(value) {
      if (this.hasDependents) {
        this.pendingScaleChange = value
        return
      }

      this.$emit('change', value)
    },

    handleScaleChange(value) {
      if (!this.hasDependents) {
        return
      }

      const change = this.pendingScaleChange
        ? { ...this.pendingScaleChange, ...value }
        : value
      this.pendingScaleChange = null

      this.$emit('requestConditionalRemovalConfirmation', { change })
    },

    handleRulesChange({ visibilityRules, hasPendingDraft = false }) {
      this.editorHasPendingDraft = hasPendingDraft
      this.$emit('change', { visibilityRules })
    },

    // A pending-only discard means the user backed out of the first rule:
    // the editor closes without emitting anything upstream.
    handleConditionDiscard() {
      this.conditionEditorActive = false
    },

    handleRuleRemove() {
      this.conditionEditorActive = false
      this.editorHasPendingDraft = false
      this.$emit('change', { visibilityRules: [] })
    },

    handleToggleCondition() {
      this.conditionEditorActive = !this.conditionEditorActive
    },
  },
}
</script>

<style lang="scss" scoped>
.item-container {
  position: relative;

  &:hover {
    .options-menu {
      visibility: visible;
    }
  }

  &.indented-item {
    margin-left: 32px;
  }
}

.options-menu {
  visibility: hidden;
  position: absolute;
  top: 0;
  right: -36px;
}

.item-type-icon-signature {
  margin-bottom: 1px;
}

.item-type-label {
  display: inline-block;
  line-height: 1.3;
  padding-bottom: 1px;
}
</style>
